import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAppContext } from '../../context/AppContextCompat';
import { CompanyService } from '../../services/api/companyService';
import { supabase } from '../../services/supabase/client';
import {
  isCompanyPremiumActive,
  isCompanyPremiumExpired,
  isCompanyStaffUserType,
} from '../../utils/companyPremium';
import { syncCompanyPremiumExpiryStrict } from '../../services/premium/companyPremiumExpirySync';
import LoadingSpinner from '../ui/LoadingSpinner';
import type { Company } from '../../services/api/companyService';

interface CompanyPremiumGuardProps {
  children: ReactNode;
}

async function resolveCompanyForGuard(
  userId: string,
  userType: string
): Promise<Company | null> {
  if (userType === 'company') {
    return CompanyService.getCompanyByUserId(userId);
  }

  if (isCompanyStaffUserType(userType)) {
    const { data } = await supabase
      .from('employees')
      .select('company_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (!data?.company_id) return null;
    return CompanyService.getCompanyById(data.company_id);
  }

  return null;
}

const StaffPremiumBlocked: React.FC<{ expired?: boolean }> = ({ expired }) => (
  <div className="pt-16 min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-amber-200 p-8 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
        <AlertCircle className="h-7 w-7 text-amber-600" />
      </div>
      <h1 className="text-xl font-bold text-gray-900 mb-2">
        {expired ? '公司 Premium 已到期' : '公司尚未升級 Premium'}
      </h1>
      <p className="text-gray-600 text-sm leading-relaxed">
        {expired
          ? '公司高級會員已過期，案件與收入等功能已暫停。請聯繫公司管理員至 Premium 頁續費。'
          : '此功能需要公司管理員升級 Premium 會員後才能使用。請聯繫公司負責人完成升級。'}
      </p>
    </div>
  </div>
);

export const CompanyPremiumGuard: React.FC<CompanyPremiumGuardProps> = ({ children }) => {
  const { currentUser } = useAppContext();
  const [checking, setChecking] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [wasExpired, setWasExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!currentUser) {
        setChecking(false);
        return;
      }

      try {
        const company = await resolveCompanyForGuard(
          currentUser.id,
          currentUser.userType
        );
        if (company?.id) {
          await syncCompanyPremiumExpiryStrict(company.id);
        }
        const refreshed = company?.id
          ? await resolveCompanyForGuard(currentUser.id, currentUser.userType)
          : company;

        if (!cancelled && refreshed) {
          setIsPremium(isCompanyPremiumActive(refreshed));
          setWasExpired(isCompanyPremiumExpired(refreshed));
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  if (checking) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="正在驗證 Premium 會員..." />
      </div>
    );
  }

  if (isPremium) {
    return <>{children}</>;
  }

  if (currentUser?.userType === 'company') {
    return (
      <Navigate
        to={wasExpired ? '/company/premium?expired=1' : '/company/premium?requireUpgrade=1'}
        replace
      />
    );
  }

  if (isCompanyStaffUserType(currentUser?.userType)) {
    return <StaffPremiumBlocked expired={wasExpired} />;
  }

  return <Navigate to="/company/premium?requireUpgrade=1" replace />;
};
